import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { QuoteDetail, QuoteListResponse, QuoteStatus } from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { buildQueryString } from "@/lib/query-string";
import { useScopedClientId } from "@/hooks/useScopedClientId";
import { useQuotesStore } from "@/stores/quotes.store";

export const QUOTES_PAGE_SIZE = 20;

/**
 * The /cotizaciones table: one page under the store's filters. Scoped on
 * `useScopedClientId` (cache key AND `enabled` gate), the `useOrdersTable` rule.
 */
export function useQuotesTable() {
  const clientId = useScopedClientId();
  const search = useQuotesStore((s) => s.search);
  const statuses = useQuotesStore((s) => s.statuses);
  const page = useQuotesStore((s) => s.page);
  const statusesKey = statuses.join(",");
  return useQuery({
    queryKey: ["quotes", clientId, "list", search, statusesKey, page],
    queryFn: () =>
      api.get<QuoteListResponse>(
        `/crm/quotes?${buildQueryString({ search, statuses: statusesKey, page, pageSize: QUOTES_PAGE_SIZE })}`,
      ),
    enabled: !!clientId,
    placeholderData: keepPreviousData,
  });
}

export function useQuote(id: string) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["quotes", clientId, "detail", id],
    queryFn: () => api.get<QuoteDetail>(`/crm/quotes/${id}`),
    enabled: !!clientId && !!id,
  });
}

const STATUS_SUCCESS: Partial<Record<QuoteStatus, string>> = {
  in_progress: "Solicitud tomada. El agente ya no la modifica.",
  sent: "Marcada como enviada.",
  won: "Marcada como ganada.",
  lost: "Marcada como perdida.",
};

/** A human's move on a request. Every quote cache and the lead refresh after it. */
export function useSetQuoteStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; status: QuoteStatus; lostReason?: string | null }) =>
      api.patch<QuoteDetail>(`/crm/quotes/${vars.id}/status`, {
        status: vars.status,
        ...(vars.status === "lost" ? { lostReason: vars.lostReason ?? null } : {}),
      }),
    onSuccess: (quote) => {
      void queryClient.invalidateQueries({ queryKey: ["quotes"] });
      void queryClient.invalidateQueries({ queryKey: ["lead", quote.leadId] });
      const message = STATUS_SUCCESS[quote.status];
      if (message) toast.success(message);
    },
    onError: (err: Error) => {
      toast.error(err.message || "No se pudo cambiar el estado.");
    },
  });
}

/**
 * One lead's requests, for the lead detail. Under `["quotes", …]` so every
 * mutation above refreshes it. `enabled` is the caller's plan check — the
 * endpoint 403s a client without `quotes`.
 */
export function useLeadQuotes(leadId: string, enabled: boolean) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["quotes", clientId, "lead", leadId],
    queryFn: () =>
      api.get<QuoteListResponse>(
        `/crm/quotes?leadId=${encodeURIComponent(leadId)}&page=1&pageSize=100`,
      ),
    enabled: enabled && !!clientId && !!leadId,
  });
}
